<template>
    <div
        class="filter-mobile"
        id="mobile-categories-menu"
        @keydown.tab="checkFocusInside"
    >
        <div class="buttons-wrapper">
            <button
                ref="filterButton"
                :class="{ 'btn--bg': hasActiveFilters }"
                class="filter-mobile__btn btn"
                @click.prevent="openFilterLayer"
            >
                <span class="filter-mobile__btn-label">
                    {{ i18n('Filtration') }}
                </span>
                <FunnelIcon class="icon funnel" width="26" height="20" />
            </button>

            <LayoutOverlay
                v-if="activeLayer === 'filter'"
                :is-open="activeLayer === 'filter'"
                :triggerDataFilterName="triggerDataFilterName"
                title="Filtry"
                class="filter-mobile__overlay"
                @close="closeFlitersLayer"
                @keydown.esc="closeFlitersLayer"
            >
                <template #button>
                    <button class="btn btn--small clear" @click="cleanFilters">
                        {{ i18n('Clear filters') }}
                        <span class="sr-only">Usuń filtr</span>
                        <CloseIcon width="20" aria-hidden="true" />
                    </button>
                </template>
                <div>
                    <ul class="filters-active">
                        <template
                            v-for="(
                                filterGroup, filterName
                            ) in getFiltersActive"
                        >
                            <li
                                v-for="(filter, index) in filterGroup"
                                :key="`${filterName}-${index}`"
                            >
                                <button
                                    class="filters-active__button btn btn--small"
                                    @click.prevent="
                                        removeFilter({
                                            key: filterName,
                                            values: [filter.value]
                                        })
                                    "
                                >
                                    {{ filter.label || filter.filter_name }}
                                    <span class="sr-only">Usuń filtr</span>
                                    <CloseIcon width="20" aria-hidden="true" />
                                </button>
                            </li>
                        </template>
                    </ul>
                </div>
                <FilterList
                    v-if="filters"
                    :filters="filters"
                    @activeFiltersCount="setActiveFiltersCount"
                    :titleDisplay="false"
                    :triggerDataFilterName="triggerDataFilterName"
                />

                <div class="filter-mobile__apply">
                    <button
                        class="btn btn--small apply-button"
                        style="flex-grow: 5"
                        @click="closeFlitersLayer"
                        aria-label="Anuluj"
                    >
                        {{ i18n('Anuluj') }}
                    </button>
                    <button
                        class="btn btn--block btn--bg apply-button"
                        style="flex-grow: 2"
                        @click="applyFilters"
                        v-text="applyButtonDisplay"
                        aria-label="Zastosuj filtry"
                    />
                </div>
            </LayoutOverlay>

            <CatalogSort
                class="filter-mobile__btn"
                button-only
                @click.native="activeLayer = 'sort'"
            >
                {{ i18n('Sorting') }}
            </CatalogSort>
            <button
                class="filter-mobile__btn btn"
                @click.prevent="activeLayer = 'categories'"
            >
                <span class="filter-mobile__btn-label">
                    {{ i18n('Category') }}
                </span>
                <SortIcon class="icon arrow" width="9" height="10" />
            </button>
        </div>

        <LayoutOverlay
            v-if="activeLayer === 'sort'"
            :is-open="activeLayer === 'sort'"
            title="Sortuj"
            class="filter-mobile__overlay"
            @close="activeLayer = null"
            @keydown.esc="closeLayer"
        >
            <CatalogSortMobile @mobileCloseSort="mobileCloseSortCallback" />
        </LayoutOverlay>

        <LayoutOverlay
            v-if="activeLayer === 'categories'"
            :is-open="activeLayer === 'categories'"
            :return-button-visible="false"
            class="filter-mobile__overlay-background"
            @close="activeLayer = null"
            tabindex="-1"
            ref="focusTrapContainer"
            @keydown.esc="closeLayer"
        >
            <template>
                <div class="categories-wrapper">
                    <div class="categories-header">
                        <div class="categories-header__wrapper">
                            <div
                                v-if="!isMainCategory"
                                class="back-button"
                                @click="goBack(currentCategory)"
                                @keydown.enter.prevent="goBack(currentCategory)"
                                @keydown.space.prevent="goBack(currentCategory)"
                                tabindex="0"
                                aria-label="Powrót"
                            >
                                <ArrowIcon width="16" aria-hidden="true" />
                            </div>
                            <div class="title" v-text="currentCategory.name" />
                            <div
                                id="close-category-modal"
                                class="close-button"
                                @click="activeLayer = null"
                                tabindex="0"
                                role="button"
                                @keydown.enter.prevent="activeLayer = null"
                                @keydown.space.prevent="activeLayer = null"
                                aria-label="Zamknij menu kategorii"
                            >
                                <CloseIcon width="30" aria-hidden="true" />
                            </div>
                        </div>
                        <a
                            class="categories-link"
                            :href="currentCategory.url"
                            aria-label="Zobacz całą kategorię"
                        >
                            Zobacz całą kategorię
                        </a>
                    </div>
                    <ul class="categories-content" tabindex="-1">
                        <li
                            v-for="category in currentCategoryChildren"
                            tabindex="0"
                            :key="category.id"
                            :aria-label="'Kategoria:' + category.name"
                            :role="
                                category.children &&
                                category.children.length > 0
                                    ? 'button'
                                    : 'link'
                            "
                            class="category-list-element"
                            @click="goToCategory(category)"
                            @keydown.enter.prevent="goToCategory(category)"
                            @keydown.space.prevent="goToCategory(category)"
                        >
                            <div
                                class="category-title"
                                v-text="category.name"
                            />
                            <SortIcon
                                v-if="
                                    category.children &&
                                    category.children.length > 0
                                "
                                class="category-button"
                                width="24"
                                aria-hidden="true"
                            />
                        </li>
                    </ul>
                </div>
            </template>
        </LayoutOverlay>
    </div>
</template>
<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import FilterList from '../FilterList.vue'
import CatalogSort, { CatalogSortMobile } from '../../CatalogSort'
import { LayoutOverlay } from '../../Layout'
import SortIcon from '@icons/sort.svg'
import FunnelIcon from '@icons/filter-funnel.svg'
import CloseIcon from '@icons/close.svg'
import ArrowIcon from '@icons/sort_arrow.svg'

export default {
    components: {
        LayoutOverlay,
        FilterList,
        CatalogSortMobile,
        CatalogSort,
        SortIcon,
        FunnelIcon,
        CloseIcon,
        ArrowIcon
    },
    props: {
        triggerMobileFilter: { type: [String, Number], default: 0 },
        triggerDataFilterName: { type: String, default: '' }
    },
    data() {
        return {
            activeLayer: '',
            activeFiltersCount: 0,
            currentChosenCategory: '',
            lastActiveButton: null
        }
    },
    mounted() {
        document
            .getElementById('mobile-categories-menu')
            .addEventListener('keydown', this.checkFocusInside)

        document.addEventListener('keydown', this.handleEscKey)
    },
    beforeDestroy() {
        document
            .getElementById('mobile-categories-menu')
            .removeEventListener('keydown', this.checkFocusInside)

        document.removeEventListener('keydown', this.handleEscKey)
    },
    computed: {
        ...mapState({
            filters: (state) => state.filters,
            filtersActive: (state) => state.filtersActive,
            categoryList: ({ category }) => category,
            categoryTree: ({ options }) => options.category.categoryTree
        }),
        ...mapGetters(['i18n', 'getFiltersActive']),
        hasActiveFilters() {
            return Object.keys(this.filtersActive).length
        },
        applyButtonDisplay() {
            return this.activeFiltersCount > 0
                ? `${this.i18n('Apply filters')} (${this.activeFiltersCount})`
                : `${this.i18n('Apply filters')}`
        },
        currentCategory() {
            return this.currentChosenCategory || this.categoryList.current
        },
        currentCategoryChildren() {
            return (
                this.currentChosenCategory?.children ||
                this.categoryList?.children
            )
        },
        currentCategoryParentID() {
            if (!this.categoryList.parent) return null
            return (
                this.currentCategory?.parent_id || this.categoryList.parent.id
            )
        },
        isMainCategory() {
            return Object.keys(this.categoryTree).includes(
                this.currentCategory.id.toString()
            )
        }
    },
    watch: {
        triggerMobileFilter() {
            this.activeLayer = 'filter'
            this.dispatchFilters({
                filters: this.filtersActive,
                preselection: true
            })
            document.getElementById('chat-widget-container').style.visibility =
                'hidden'
        },
        triggerDataFilterName(newValue) {
            this.handleTriggerDataFilterName(newValue)
        },
        activeLayer(layer) {
            if (layer === 'filter') {
                this.dispatchFilters({
                    filters: this.filtersActive,
                    preselection: true
                })
                document.getElementById(
                    'chat-widget-container'
                ).style.visibility = 'hidden'
            }

            if (layer === '' || layer === null) {
                this.currentChosenCategory = ''
                document.getElementById(
                    'chat-widget-container'
                ).style.visibility = 'visible'

                if (this.lastActiveButton) {
                    this.$nextTick(() => {
                        this.lastActiveButton.focus()
                    })
                }
            }
        }
    },
    methods: {
        ...mapActions([
            'dispatchFilters',
            'dispatchPreselectedFilters',
            'cleanFilters',
            'removeFilter'
        ]),
        checkFocusInside() {
            const focusTrapContainer = this.$refs.focusTrapContainer
            if (focusTrapContainer && focusTrapContainer.$el) {
                var overlay = focusTrapContainer.$el

                if (overlay && overlay.contains(document.activeElement)) {
                    this.$nextTick(() => this.activateFocusTrap())
                } else {
                    this.deactivateFocusTrap()
                }
            }
        },
        activateFocusTrap() {
            const overlay = this.$refs.focusTrapContainer.$el
            if (!overlay) return

            if (this._focusTrapHandler) {
                overlay.removeEventListener('keydown', this._focusTrapHandler)
            }

            const focusableSelectors = [
                'a[href]:not([disabled])',
                'button:not([disabled])',
                'li[tabindex]:not([tabindex="-1"])',
                'ul[tabindex]:not([tabindex="-1"])',
                '[tabindex]:not([tabindex="-1"])'
            ]

            const focusableEls = Array.from(
                overlay.querySelectorAll(focusableSelectors.join(','))
            )
            if (!focusableEls.length) return

            if (!overlay.contains(document.activeElement)) {
                focusableEls[0].focus()
            }

            this._focusTrapHandler = (e) => {
                if (e.key !== 'Tab') return

                const first = focusableEls[0]
                const last = focusableEls[focusableEls.length - 1]

                if (e.shiftKey) {
                    if (document.activeElement === first) {
                        e.preventDefault()
                        last.focus()
                    }
                } else {
                    if (document.activeElement === last) {
                        e.preventDefault()
                        first.focus()
                    }
                }
            }
            overlay.addEventListener('keydown', this._focusTrapHandler)
        },

        deactivateFocusTrap() {
            const overlay = this.$refs.focusTrapContainer?.$el
            if (this._focusTrapHandler && overlay) {
                overlay.removeEventListener('keydown', this._focusTrapHandler)
                this._focusTrapHandler = null
            }
        },
        handleTriggerDataFilterName(triggerDataFilterName) {
            setTimeout(function () {
                const inputElement = document.querySelectorAll(
                    `.filter-list--checkbox[data-filter-name="${triggerDataFilterName}"]`
                )
                const scrollableParent = document.getElementsByClassName(
                    'filter-mobile__overlay'
                )
                if (scrollableParent) {
                    scrollableParent[0].scrollTo({
                        top: inputElement[1].offsetTop,
                        behavior: 'smooth'
                    })
                }
            }, 500)
        },
        findObjectById(data, targetId) {
            let result = null

            data.forEach((item) => {
                if (item.id == targetId) {
                    result = item // Found the object
                    return
                }
                // If 'children' exists and is an array or object
                if (item.children) {
                    const childrenArray = Array.isArray(item.children)
                        ? item.children
                        : Object.values(item.children)

                    // Recursively search in children
                    const childResult = this.findObjectById(
                        childrenArray,
                        targetId
                    )
                    if (childResult) {
                        result = childResult // Found in children
                        return
                    }
                }
            })

            return result
        },
        applyFilters() {
            this.dispatchPreselectedFilters()
            this.closeFlitersLayer()
        },
        closeFlitersLayer() {
            this.cleanFilters({ preselection: true })
            this.activeLayer = ''
        },
        setActiveFiltersCount(count) {
            this.activeFiltersCount = count
        },
        goToCategory(category) {
            if (!category.children || category.children.length == 0)
                return (window.location.href = category.url)
            this.currentChosenCategory = category
            document.getElementById('close-category-modal').focus()
            this.$nextTick(() => this.activateFocusTrap())
        },
        goBack() {
            if (this.currentCategoryParentID === null)
                return (this.activeLayer = null)
            this.currentChosenCategory = this.findObjectById(
                Object.values(this.categoryTree),
                this.currentCategoryParentID
            )
            this.$nextTick(() => this.activateFocusTrap())
        },
        mobileCloseSortCallback() {
            this.activeLayer = ''
        },

        handleEscKey(event) {
            if (event.key === 'Escape' && this.activeLayer) {
                if (this.activeLayer === 'filter') {
                    this.closeFlitersLayer()
                } else {
                    this.activeLayer = null
                }
            }
        },

        openFilterLayer() {
            this.lastActiveButton = this.$refs.filterButton
            this.activeLayer = 'filter'
        },

        openSortLayer() {
            this.lastActiveButton = this.$refs.sortButton
            this.activeLayer = 'sort'
        },

        openCategoriesLayer() {
            this.lastActiveButton = this.$refs.categoriesButton
            this.activeLayer = 'categories'
        },

        closeLayer() {
            this.activeLayer = null
        }
    }
}
</script>
<style scoped lang="scss">
#chat-widget-container {
    z-index: 10000 !important;
}

.filter-mobile {
    background-color: $gray-light;
    padding: 8px;

    @media #{$media-md-up} {
        display: none;
    }

    @media #{$media-xs} {
        margin-top: 0;
    }

    @media (max-width: 480px) {
        gap: 1.3rem;
    }

    &__overlay {
        padding-bottom: 7rem;
    }

    &__apply {
        position: fixed;
        display: flex;
        justify-content: space-between;
        gap: 16px;
        left: 0rem;
        right: 0;
        bottom: 0;
        background: #fff;
        padding: 1.5rem;

        .btn--block {
            min-width: 0;
            width: auto;
        }

        .apply-button {
            font-size: 14px;
        }

        &:before {
            content: '';
            display: block;
            position: absolute;
            height: 45px;
            background: linear-gradient(
                360deg,
                white 25%,
                rgba(255, 255, 255, 0)
            );
            left: 0;
            right: 0;
            bottom: 100%;
            margin-bottom: -1.5rem;
            pointer-events: none;
        }
    }

    &__sort-icon {
        margin-right: 0;
    }

    &__btn-label {
        font-size: 14px;
        font-weight: 600;
    }

    &__btn {
        min-width: 0;
        width: auto;
        height: 40px;
        min-height: 40px;
        border-radius: $button-radius;
        background-color: #fff;

        .btn {
            height: 40px;
            min-width: 0;
            width: auto;
        }

        &:hover,
        &:active {
            svg {
                color: white;
            }
        }

        &.btn--bg {
            background-color: $sherpa-blue;

            .funnel {
                path {
                    fill: white;
                }
            }
        }

        svg {
            height: 2rem;
            color: $sherpa-blue;
        }
    }

    .clear {
        background-color: transparent;
        border: 0;

        &:hover {
            background-color: transparent;
            color: $blue-dianne;
        }
    }

    .buttons-wrapper {
        width: 100%;
        display: flex;
        gap: 8px;
        justify-content: space-between;
    }

    .icon {
        margin-left: 0;

        &.arrow {
            margin-left: 6px;
        }

        &.funnel {
            margin-left: 0;
        }

        path {
            fill: $sherpa-blue;
        }
    }
}

.filters-active {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;

    &__button {
        font-size: 14px;
        height: 30px;
        min-height: 30px;
        border-radius: 25px;

        svg {
            margin-top: rem(-2px);
            margin-left: rem(2px);
        }
    }
}

.filter-mobile__overlay-background {
    background-color: rgba(0, 0, 0, 0.3);
}

.categories-wrapper {
    position: relative;
    height: 95%;
    width: 100%;
    margin: 8px 0;
    background-color: #fff;
    border-radius: 4px;
    border: 1px solid #cee7d8;
    overflow-y: hidden;

    .categories-header {
        position: relative;
        min-height: 80px;
        padding: 8px 0px;
        margin: 0 10px;
        border-bottom: 1px solid #cee7d8;

        .categories-header__wrapper {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .back-button {
            margin-left: 8px;
            width: 20px;
            height: 24px;

            svg {
                transform: rotate(90deg);

                path {
                    fill: #007f32;
                }
            }
        }

        .close-button {
            height: 30px;
            svg {
                path {
                    fill: #007f32;
                }
            }
        }

        .title {
            margin: 4px;
            text-align: center;
            font-size: 20px;
            font-weight: 600;
            line-height: 22px;
        }

        .categories-link {
            position: absolute;
            bottom: 0;
            line-height: 24px;
            font-size: 12px;
            color: #007f32;
            text-decoration: underline;
        }
    }

    .categories-content {
        height: 100%;
        margin: 10px;
        overflow-y: auto;

        .category-list-element {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 13px 8px;
            border-bottom: 1px solid #eaeaee;
        }

        .category-title {
            line-height: 22px;
            font-size: 16px;
            font-weight: 600;
            max-width: 90%;
        }

        .category-button {
            height: 12px;
            width: 12px;
            transform: rotate(-90deg);

            path {
                fill: #9ea4b3;
            }
        }
    }
}
</style>
